import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ManualMeetingDialog,
  type ManualMeetingValues,
} from '@/components/meetings/ManualMeetingDialog.tsx';
import { fetchColleagues } from '@/lib/colleagues.ts';
import { createManualMeeting } from '@/lib/meetings.ts';
import { fetchMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import { m } from '@/paraglide/messages.js';

export interface AddMeetingDialogProps {
  userId: string;
  open: boolean;
  /** Called when the dialog is done with, whether it saved or was waved away. */
  onClose: () => void;
}

/**
 * Writing a conversation down by hand, wherever it is asked for.
 *
 * The form and everything behind it — the gesprekstypes, the names already
 * known, the save — live here rather than on the button, because the agenda's
 * "Gesprek toevoegen" is not the only door: the chat drawer's first nudge opens
 * the same one, and two copies of this would drift.
 *
 * The dialog closes and the week is read again, rather than the new meeting
 * being pushed into the list by hand — where it lands in the week is the
 * server's answer, and a client that guesses it would show the meeting twice
 * the moment the two disagree.
 */
export function AddMeetingDialog({ userId, open, onClose }: AddMeetingDialogProps) {
  const queryClient = useQueryClient();

  /* Read when the dialog is opened: the form cannot ask before it is on screen. */
  const { data: templates = [] } = useQuery({
    queryKey: ['meetingTemplates', userId, 'use'],
    queryFn: () => fetchMeetingTemplates(userId, 'use'),
    enabled: open,
  });

  /* The same list the team page draws, for the same reason: these are the names. */
  const { data: colleagues = [] } = useQuery({
    queryKey: ['colleagues', userId, 'active'],
    queryFn: () => fetchColleagues(userId, { hidden: false }),
    enabled: open,
  });

  const add = useMutation({
    mutationFn: (values: ManualMeetingValues) =>
      createManualMeeting(userId, {
        title: values.title,
        starts_at: values.startsAt,
        template_id: values.templateId,
        attendees: values.attendees.map((name) => ({ name })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      /* A name typed here is a colleague from now on; the team page has to hear it. */
      await queryClient.invalidateQueries({ queryKey: ['colleagues'] });
      /* And the drawer's first nudge is about having none, which is now untrue. */
      await queryClient.invalidateQueries({ queryKey: ['onboardingNudge'] });
      close();
    },
  });

  const close = () => {
    add.reset();
    onClose();
  };

  return (
    <ManualMeetingDialog
      open={open}
      title={m.agenda_add_meeting()}
      saveLabel={m.manual_meeting_save()}
      templates={templates}
      withPeople={{
        suggestions: colleagues.map((colleague) => colleague.label),
      }}
      pending={add.isPending}
      failed={add.isError}
      onSubmit={(values) => add.mutate(values)}
      onCancel={close}
    />
  );
}
